import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { searchPlaces } from '../../api/geocode';
import { env } from '../../config/env';
import { useDebounce } from '../../hooks/useDebounce';
import '../../utils/leafletSetup';
import { DEFAULT_MAP_CENTER } from '../../utils/leafletSetup';

const MapClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
};

const MapResize = ({ active }) => {
  const map = useMap();
  useEffect(() => {
    if (!active) return undefined;
    const resize = () => map.invalidateSize({ animate: false });
    resize();
    const t1 = window.setTimeout(resize, 100);
    const t2 = window.setTimeout(resize, 400);
    window.addEventListener('resize', resize);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', resize);
    };
  }, [active, map]);
  return null;
};

const MapFlyTo = ({ target, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], zoom, { duration: 0.8 });
  }, [target, zoom, map]);
  return null;
};

const LocationMapPicker = ({
  value,
  onChange,
  onPlaceSelect,
  searchDefault = '',
  active = true,
  height = '280px',
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState(value || null);
  const [mapReady, setMapReady] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [flyTarget, setFlyTarget] = useState(null);
  const [mapZoom, setMapZoom] = useState(14);
  const debouncedSearch = useDebounce(searchInput, 450);

  const { data: searchData, isFetching: searchLoading } = useQuery({
    queryKey: ['geocode-search', debouncedSearch],
    queryFn: () => searchPlaces(debouncedSearch),
    enabled: active && debouncedSearch.trim().length >= 2,
    staleTime: 60_000,
  });

  const searchResults = searchData?.results || [];

  useEffect(() => {
    if (!active) {
      setMapReady(false);
      setFlyTarget(null);
      return undefined;
    }
    setSearchInput(searchDefault || '');
    const timer = window.setTimeout(() => setMapReady(true), 50);
    return () => window.clearTimeout(timer);
  }, [active, searchDefault]);

  useEffect(() => {
    setPosition(value || null);
    if (value?.latitude != null && value?.longitude != null) {
      setFlyTarget({ lat: value.latitude, lng: value.longitude });
      setMapZoom(16);
    }
  }, [value]);

  const mapCenter = useMemo(() => {
    if (flyTarget) return flyTarget;
    if (position?.latitude != null && position?.longitude != null) {
      return { lat: position.latitude, lng: position.longitude };
    }
    return DEFAULT_MAP_CENTER;
  }, [flyTarget, position]);

  const handlePick = (coords) => {
    setPosition(coords);
    onChange?.(coords);
  };

  const selectPlace = (place) => {
    const coords = { latitude: place.latitude, longitude: place.longitude };
    setSearchInput(place.label);
    setFlyTarget({ lat: place.latitude, lng: place.longitude });
    setMapZoom(16);
    handlePick(coords);
    onPlaceSelect?.(place.label, coords);
  };

  return (
    <div className="rounded-xl border border-zinc-300 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-950/50">
      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('salon.branches.mapLocation')}</p>
      <p className="mt-1 text-xs text-zinc-500">{t('salon.branches.mapLocationHint')}</p>

      <div className="relative mt-3">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('salon.branches.searchLocationPlaceholder')}
          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-glow-500 dark:border-zinc-700 dark:bg-zinc-900"
        />
        {searchLoading && (
          <span className="absolute right-3 top-2.5 text-xs text-zinc-500">{t('common.working')}</span>
        )}
        {searchResults.length > 0 && searchInput.trim().length >= 2 && (
          <ul className="absolute z-[1000] mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-zinc-300 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
            {searchResults.map((place) => (
              <li key={`${place.latitude}-${place.longitude}-${place.label}`}>
                <button
                  type="button"
                  onClick={() => selectPlace(place)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  {place.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        className="relative mt-3 overflow-hidden rounded-lg border border-zinc-300 dark:border-zinc-700"
        style={{ height, minHeight: height }}
      >
        {!mapReady ? (
          <div className="flex h-full items-center justify-center bg-zinc-100 text-sm text-zinc-500 dark:bg-zinc-900">
            {t('explore.loadingMap')}
          </div>
        ) : (
          <MapContainer
            key={`branch-map-${active ? 'open' : 'closed'}`}
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={mapZoom}
            className="branch-location-map h-full w-full"
            style={{ height: '100%', width: '100%', minHeight: height }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url={env.mapTileUrl}
            />
            <MapClickHandler onPick={handlePick} />
            <MapResize active={active} />
            <MapFlyTo target={flyTarget} zoom={mapZoom} />
            {position?.latitude != null && position?.longitude != null && (
              <Marker
                position={[position.latitude, position.longitude]}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    handlePick({ latitude: lat, longitude: lng });
                  },
                }}
              />
            )}
          </MapContainer>
        )}
      </div>

      {position?.latitude != null && position?.longitude != null ? (
        <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
          {t('salon.branches.coordsSet', {
            lat: position.latitude.toFixed(5),
            lng: position.longitude.toFixed(5),
          })}
        </p>
      ) : (
        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
          {t('salon.branches.coordsRequired')}
        </p>
      )}
      <p className="mt-1 text-xs text-zinc-500">{t('salon.branches.mapFineTuneHint')}</p>
    </div>
  );
};

export default LocationMapPicker;
